import openai from './openaiClient';
import { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Generates comprehensive Thai language interpretation of astrological chart data.
 * @param {Object} chartData - Complete astrological chart data
 * @returns {Promise<Object>} AI-generated interpretations in Thai
 */
export async function generateChartInterpretation(chartData) {
  try {
    const { planetaryPositions, aspects, houseCusps } = chartData;

    // Build detailed prompt for Thai astrological interpretation
    const planetaryInfo = planetaryPositions?.map(p => 
      `${p?.nameTh} (${p?.nameEn}) อยู่ที่ ${p?.degree}° ในราศี${p?.zodiacTh} (${p?.zodiacEn}) บ้านที่ ${p?.house}`
    )?.join('\n');

    const aspectsInfo = aspects?.map(a => 
      `${a?.planetA} ${a?.aspectTypeTh} (${a?.aspectTypeEn}) ${a?.planetB} ค่าเบี่ยงเบน ${a?.orb}°`
    )?.join('\n');

    const housesInfo = houseCusps?.map(h => 
      `บ้านที่ ${h?.houseNumber}: ${h?.cuspDegree}° ในราศี${h?.zodiacTh} (${h?.zodiacEn})`
    )?.join('\n');

    const prompt = `คุณเป็นนักโหราศาสตร์มืออาชีพที่เชี่ยวชาญในการวิเคราะห์แผนภูมิดาวเกิด กรุณาวิเคราะห์ข้อมูลดาวเกิดต่อไปนี้และให้คำแนะนำเชิงลึกในภาษาไทยที่เข้าใจง่าย เหมาะสำหรับรายงานลูกค้า:

**ตำแหน่งดาวเคราะห์:**
${planetaryInfo}

**มุมระหว่างดาว (Aspects):**
${aspectsInfo}

**จุดเริ่มต้นบ้าน (House Cusps):**
${housesInfo}

กรุณาวิเคราะห์และให้คำแนะนำในหัวข้อต่อไปนี้:
1. **บุคลิกภาพและอัตลักษณ์**: วิเคราะห์ตำแหน่งดาวอาทิตย์ ดวงจันทร์ และจุดขึ้น (Ascendant)
2. **จุดแข็งและความสามารถพิเศษ**: วิเคราะห์มุมที่เป็นบวก (Trine, Sextile) และตำแหน่งดาวที่เด่น
3. **ความท้าทายและบทเรียนชีวิต**: วิเคราะห์มุมที่ท้าทาย (Square, Opposition) และตำแหน่งดาวที่ต้องพัฒนา
4. **ความสัมพันธ์และการสื่อสาร**: วิเคราะห์ดาวศุกร์ พุธ และบ้านที่ 7
5. **อาชีพและเป้าหมายชีวิต**: วิเคราะห์จุดสูงสุด (MC) บ้านที่ 10 และดาวเสาร์
6. **คำแนะนำสำหรับการพัฒนาตนเอง**: ให้คำแนะนำเชิงปฏิบัติที่เฉพาะเจาะจง

ใช้ภาษาไทยที่อ่อนโยน เข้าใจง่าย และให้กำลังใจ เหมาะสำหรับรายงานมืออาชีพ`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        { 
          role: 'system', 
          content: 'คุณเป็นนักโหราศาสตร์มืออาชีพที่มีความเชี่ยวชาญในการวิเคราะห์แผนภูมิดาวเกิดแบบตะวันตก ให้คำแนะนำที่ลึกซึ้ง เข้าใจง่าย และเป็นมิตร เหมาะสำหรับรายงานลูกค้าในภาษาไทย' 
        },
        { role: 'user', content: prompt },
      ],
      reasoning_effort: 'high',
      verbosity: 'high',
      max_completion_tokens: 3000
    });

    return {
      success: true,
      interpretation: response?.choices?.[0]?.message?.content,
      timestamp: new Date()?.toISOString()
    };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating chart interpretation:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Generates specific interpretation for planetary positions.
 * @param {Array} planetaryPositions - Array of planetary position data
 * @returns {Promise<Object>} AI-generated planetary interpretation in Thai
 */
export async function generatePlanetaryInterpretation(planetaryPositions) {
  try {
    const planetaryInfo = planetaryPositions?.map(p => 
      `${p?.nameTh} (${p?.nameEn}) อยู่ที่ ${p?.degree}° ในราศี${p?.zodiacTh} (${p?.zodiacEn}) บ้านที่ ${p?.house}`
    )?.join('\n');

    const prompt = `วิเคราะห์ตำแหน่งดาวเคราะห์ต่อไปนี้และอธิบายความหมายในภาษาไทยที่เข้าใจง่าย:

${planetaryInfo}

กรุณาอธิบายความหมายของแต่ละดาวเคราะห์ในราศีและบ้านที่อยู่ โดยเน้นที่:
- อิทธิพลต่อบุคลิกภาพและพฤติกรรม
- ความสามารถและจุดแข็งที่เด่นชัด
- ด้านชีวิตที่ได้รับอิทธิพล

ใช้ภาษาไทยที่อ่อนโยนและให้กำลังใจ`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: 'คุณเป็นนักโหราศาสตร์ที่เชี่ยวชาญในการอธิบายความหมายของตำแหน่งดาวเคราะห์ในภาษาไทยที่เข้าใจง่าย' 
        },
        { role: 'user', content: prompt },
      ],
      reasoning_effort: 'medium',
      verbosity: 'medium',
      max_completion_tokens: 2000
    });

    return {
      success: true,
      interpretation: response?.choices?.[0]?.message?.content
    };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating planetary interpretation:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Generates specific interpretation for aspects.
 * @param {Array} aspects - Array of aspect data
 * @returns {Promise<Object>} AI-generated aspects interpretation in Thai
 */
export async function generateAspectsInterpretation(aspects) {
  try {
    const aspectsInfo = aspects?.map(a => 
      `${a?.planetA} ${a?.aspectTypeTh} (${a?.aspectTypeEn}) ${a?.planetB} ค่าเบี่ยงเบน ${a?.orb}° (${a?.isMajor ? 'มุมหลัก' : 'มุมรอง'})`
    )?.join('\n');

    const prompt = `วิเคราะห์มุมระหว่างดาวต่อไปนี้และอธิบายความหมายในภาษาไทย:

${aspectsInfo}

กรุณาอธิบาย:
- ความหมายของแต่ละมุมระหว่างดาว
- อิทธิพลต่อบุคลิกภาพและชีวิต
- มุมที่เป็นบวก (จุดแข็ง) และมุมที่ท้าทาย (บทเรียน)
- คำแนะนำในการใช้พลังงานของมุมเหล่านี้

ใช้ภาษาไทยที่เข้าใจง่ายและสร้างสรรค์`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: 'คุณเป็นนักโหราศาสตร์ที่เชี่ยวชาญในการวิเคราะห์มุมระหว่างดาว (Aspects) และอธิบายในภาษาไทยที่เข้าใจง่าย' 
        },
        { role: 'user', content: prompt },
      ],
      reasoning_effort: 'medium',
      verbosity: 'medium',
      max_completion_tokens: 2000
    });

    return {
      success: true,
      interpretation: response?.choices?.[0]?.message?.content
    };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating aspects interpretation:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Generates specific interpretation for house placements.
 * @param {Array} houseCusps - Array of house cusp data
 * @param {Array} planetaryPositions - Array of planetary positions for house analysis
 * @returns {Promise<Object>} AI-generated house interpretation in Thai
 */
export async function generateHouseInterpretation(houseCusps, planetaryPositions) {
  try {
    const housesInfo = houseCusps?.map(h => 
      `บ้านที่ ${h?.houseNumber}: ${h?.cuspDegree}° ในราศี${h?.zodiacTh} (${h?.zodiacEn})`
    )?.join('\n');

    const planetsInHouses = planetaryPositions?.map(p => 
      `${p?.nameTh} อยู่ในบ้านที่ ${p?.house}`
    )?.join('\n');

    const prompt = `วิเคราะห์บ้านโหราศาสตร์และตำแหน่งดาวในบ้านต่อไปนี้:

**จุดเริ่มต้นบ้าน:**
${housesInfo}

**ดาวเคราะห์ในบ้าน:**
${planetsInHouses}

กรุณาอธิบาย:
- ความหมายของแต่ละบ้านและด้านชีวิตที่เกี่ยวข้อง
- อิทธิพลของดาวเคราะห์ในแต่ละบ้าน
- ด้านชีวิตที่โดดเด่นและต้องให้ความสนใจ
- คำแนะนำสำหรับการพัฒนาในแต่ละด้าน

ใช้ภาษาไทยที่เข้าใจง่ายและเป็นประโยชน์`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { 
          role: 'system', 
          content: 'คุณเป็นนักโหราศาสตร์ที่เชี่ยวชาญในการวิเคราะห์บ้านโหราศาสตร์และอธิบายในภาษาไทยที่เข้าใจง่าย' 
        },
        { role: 'user', content: prompt },
      ],
      reasoning_effort: 'medium',
      verbosity: 'medium',
      max_completion_tokens: 2000
    });

    return {
      success: true,
      interpretation: response?.choices?.[0]?.message?.content
    };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating house interpretation:', error);
    }
    throw new Error(errorInfo.message);
  }
}

export default {
  generateChartInterpretation,
  generatePlanetaryInterpretation,
  generateAspectsInterpretation,
  generateHouseInterpretation
};